import { useEffect, useState } from "react";
import { apiErrorMessage } from "../utils/helpers";

// Fetches an admin-toggleable settings object once on mount and exposes an
// optimistic partial save (`save`) plus a per-key boolean flip (`toggle`) built on
// it, with an auto-dismissing success/error message. Shared by any settings card
// backed by a GET/PUT app_setting pair (e.g. login methods, event promotion).
// Use `save` directly for non-boolean fields, e.g. a text input with its own
// draft state that persists on blur or an explicit Save button.
export function useToggleableSetting(getFn, updateFn, { loadErrorMessage, updateErrorMessage, successMessage }) {
  const [value, setValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    getFn()
      .then((data) => { if (active) setValue(data); })
      .catch(() => { if (active) setError(loadErrorMessage); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(t);
  }, [message]);

  const save = async (patch) => {
    if (!value || saving) return;
    const previous = value;
    // Optimistic update, revert on failure
    setValue((prev) => ({ ...prev, ...patch }));
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await updateFn(patch);
      setValue(updated);
      setMessage(successMessage);
    } catch (err) {
      setValue(previous);
      setError(apiErrorMessage(err, updateErrorMessage));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => save({ [key]: !value?.[key] });

  return { value, loading, saving, message, error, toggle, save };
}
